import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { FollowUpClosePromptReason } from '../../lib/crm/followUpClosePrompt';
import OpportunityFollowUpSchedulePanel, {
  followUpInHours,
} from './OpportunityFollowUpSchedulePanel';

interface OpportunityFollowUpPromptModalProps {
  isOpen: boolean;
  reason: FollowUpClosePromptReason;
  opportunityTitle: string;
  saving?: boolean;
  onDismiss: () => void;
  onSave: (followUpAt: string) => Promise<void>;
}

const PROMPT_COPY: Record<
  FollowUpClosePromptReason,
  { title: string; subtitle: string }
> = {
  fresh: {
    title: 'Schedule a follow-up?',
    subtitle: 'Set when to follow up on this lead before you move on.',
  },
  due_after_activity: {
    title: 'Schedule your next follow-up?',
    subtitle: 'This follow-up was due and you reached out — pick the next time to follow up.',
  },
};

export default function OpportunityFollowUpPromptModal({
  isOpen,
  reason,
  opportunityTitle,
  saving = false,
  onDismiss,
  onSave,
}: OpportunityFollowUpPromptModalProps) {
  const [customDraft, setCustomDraft] = useState('');

  if (!isOpen) return null;

  const copy = PROMPT_COPY[reason];

  const handleQuickSelect = (hours: number) => {
    void onSave(followUpInHours(hours));
  };

  const handleCustomCommit = (value: string) => {
    if (!value.trim() || saving) return;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return;
    void onSave(parsed.toISOString());
  };

  return createPortal(
    <div className="fixed inset-0 z-[10050] flex items-end sm:items-center justify-center sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => !saving && onDismiss()}
        aria-label="Close"
      />
      <div
        className="relative w-full sm:max-w-md bg-white sm:rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-100">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900">{copy.title}</h2>
            <p className="text-xs text-gray-500 mt-0.5 truncate">{opportunityTitle}</p>
            <p className="text-sm text-gray-600 mt-2">{copy.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => !saving && onDismiss()}
            disabled={saving}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <OpportunityFollowUpSchedulePanel
            customDraft={customDraft}
            saving={saving}
            onCustomDraftChange={setCustomDraft}
            onQuickSelect={handleQuickSelect}
            onCustomCommit={handleCustomCommit}
          />

          <button
            type="button"
            onClick={() => !saving && onDismiss()}
            disabled={saving}
            className="mt-4 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50"
          >
            Not now
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
